import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardActionArea
} from '@mui/material';
import {
  Person,
  Dashboard as DashboardIcon,
  Logout,
  Refresh,
  Store,
  Business,
  Category,
  Event,
  PersonAdd,
  Place,
  VpnKey,
  Assignment,
  ArrowForward,
  DarkMode,
  LightMode
} from '@mui/icons-material';
import Users from './Users';
import Shops from './Shops';
import Merchants from './Merchants';
import Categories from './Categories';
import Bookings from './Bookings';
import Hotspots from './Hotspots';
import TestAccounts from './TestAccounts';
import Logs from './Logs';
import logo from '../assets/images/logo.png';
import { adminLogout } from '../services/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeMode } from '../context/ThemeContext';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export const TABS = [
  {
    key: 'dashboard',
    path: '',
    label: 'Dashboard',
    icon: <DashboardIcon sx={{ fontSize: 20 }} />,
    title: 'Dashboard Overview',
    desc: 'System summary, key metrics, and quick navigation'
  },
  {
    key: 'users',
    path: 'users',
    label: 'Users',
    icon: <Person sx={{ fontSize: 20 }} />,
    title: 'User Management',
    desc: 'Manage customer accounts, profiles, and activity'
  },
  {
    key: 'merchants',
    path: 'merchants',
    label: 'Merchants',
    icon: <Business sx={{ fontSize: 20 }} />,
    title: 'Merchant Management',
    desc: 'Review vendor profiles, approvals, and credentials'
  },
  {
    key: 'shops',
    path: 'shops',
    label: 'Shops',
    icon: <Store sx={{ fontSize: 20 }} />,
    title: 'Shop Management',
    desc: 'Configure shops, operating hours, and photo catalogs'
  },
  {
    key: 'categories',
    path: 'categories',
    label: 'Categories',
    icon: <Category sx={{ fontSize: 20 }} />,
    title: 'Categories & Types',
    desc: 'Define service classifications, tags, and category types'
  },
  {
    key: 'bookings',
    path: 'bookings',
    label: 'Bookings',
    icon: <Event sx={{ fontSize: 20 }} />,
    title: 'Booking Management',
    desc: 'Track customer appointments, statuses, and history'
  },
  {
    key: 'hotspots',
    path: 'hotspots',
    label: 'Hotspots',
    icon: <Place sx={{ fontSize: 20 }} />,
    title: 'Hotspots & Zones',
    desc: 'Designate priority parking zones and pickup/drop areas'
  },
  {
    key: 'test-accounts',
    path: 'test-accounts',
    label: 'Test Accounts',
    icon: <VpnKey sx={{ fontSize: 20 }} />,
    title: 'Review Bot Accounts',
    desc: 'Manage credentials for Apple and Google app store verification'
  },
  {
    key: 'logs',
    path: 'logs',
    label: 'System Logs',
    icon: <Assignment sx={{ fontSize: 20 }} />,
    title: 'System Logs & Audit',
    desc: 'Inspect real-time activity events, error stack traces, and 20-day retention'
  },
];

function Dashboard({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [refreshKey, setRefreshKey] = useState(0);
  const { isDarkMode, toggleTheme } = useThemeMode();

  // Derive active tab from URL pathname (e.g. "/logs" -> "logs", "/users" -> "users", "/" -> "")
  const pathSegment = location.pathname.replace(/^\//, '').split('/')[0].toLowerCase();
  const normalizedPath = (pathSegment === 'system-logs' || pathSegment === 'log')
    ? 'logs'
    : ((pathSegment === 'dashboard' || pathSegment === 'overview') ? '' : pathSegment);

  const matchedIndex = TABS.findIndex(t => t.path === normalizedPath);
  const currentTab = matchedIndex >= 0 ? matchedIndex : 0;

  // Sync document title with current page
  useEffect(() => {
    const currentItem = TABS[currentTab];
    if (currentItem) {
      document.title = `${currentItem.title} | Akros Admin`;
    }
  }, [currentTab]);

  const handleTabChange = (event, newValue) => {
    const target = TABS[newValue];
    if (target) {
      navigate(target.path ? `/${target.path}` : '/');
    }
  };

  const handleNavigateToTab = (path) => {
    navigate(path ? `/${path}` : '/');
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleLogout = () => {
    adminLogout();
    onLogout();
  };

  const handleRegister = () => {
    navigate('/register');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      p: 4,
      background: 'transparent'
    }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          background: isDarkMode 
            ? 'linear-gradient(145deg, #1e293b 0%, #172033 100%)' 
            : 'linear-gradient(145deg, #fafcfb 0%, #f1f5f3 100%)',
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0,0,0,0.08)',
          boxShadow: isDarkMode ? '0 4px 25px rgba(0,0,0,0.25)' : '0 4px 20px rgba(0,0,0,0.05)',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              onClick={() => handleNavigateToTab('')}
              sx={{
                width: 140,
                height: 'auto',
                borderRadius: 1,
                objectFit: 'contain',
                cursor: 'pointer'
              }}
            />
            <Box>
              <Typography 
                variant="h6" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  color: isDarkMode ? '#e2e8f0' : '#1e293b',
                  lineHeight: 1.2
                }}
              >
                Admin Panel
              </Typography>
              <Typography variant="caption" sx={{ color: '#52ad83', fontWeight: 600 }}>
                {TABS[currentTab]?.title || 'Dashboard Overview'}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Dark Theme Toggle Button on Top */}
            <Tooltip title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
              <IconButton
                onClick={toggleTheme}
                sx={{
                  color: isDarkMode ? '#fbbf24' : '#64748b',
                  background: isDarkMode ? 'rgba(251, 191, 36, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                  border: isDarkMode ? '1px solid rgba(251, 191, 36, 0.25)' : '1px solid rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    color: isDarkMode ? '#f59e0b' : '#52ad83',
                    background: isDarkMode ? 'rgba(251, 191, 36, 0.2)' : 'rgba(82, 173, 131, 0.1)',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {isDarkMode ? <LightMode sx={{ fontSize: 20 }} /> : <DarkMode sx={{ fontSize: 20 }} />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Register New Admin">
              <IconButton
                onClick={handleRegister}
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'transparent',
                  '&:hover': { color: '#52ad83', background: 'rgba(82, 173, 131, 0.12)' },
                }}
              >
                <PersonAdd />
              </IconButton>
            </Tooltip>

            <Tooltip title="Refresh Active Page">
              <IconButton
                onClick={handleRefresh}
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'transparent',
                  '&:hover': { color: '#52ad83' },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                borderRadius: 2,
                background: isDarkMode ? 'rgba(82, 173, 131, 0.15)' : 'rgba(82, 173, 131, 0.08)',
                border: isDarkMode ? '1px solid rgba(82, 173, 131, 0.3)' : '1px solid rgba(82, 173, 131, 0.2)',
              }}
            >
              <Person sx={{ color: '#52ad83', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: isDarkMode ? '#cbd5e1' : '#334155', fontWeight: 600 }}>
                Admin
              </Typography>
            </Box>

            <Tooltip title="Logout">
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#6b7280',
                  '&:hover': { color: '#dc2626' },
                }}
              >
                <Logout />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0,0,0,0.08)', px: 3, overflowX: 'auto' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              minWidth: 'max-content',
              '& .MuiTab-root': {
                color: isDarkMode ? '#94a3b8' : '#6b7280',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                minHeight: 48,
                px: 2,
                '&.Mui-selected': {
                  color: '#52ad83',
                },
              },
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
                height: 3,
                borderRadius: 3,
              },
              '& .MuiTabs-flexContainer': {
                gap: 1,
              },
            }}
          >
            {TABS.map((tabItem, index) => (
              <Tab 
                key={tabItem.key}
                icon={tabItem.icon} 
                iconPosition="start"
                label={tabItem.label} 
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Content / Page Views */}
        <Box sx={{ p: 3 }}>
          {/* Index 0: Overview Hub */}
          <TabPanel value={currentTab} index={0} key={`panel-0-${refreshKey}`}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 3,
                background: isDarkMode 
                  ? 'linear-gradient(145deg, #1e293b 0%, #172033 100%)' 
                  : 'linear-gradient(145deg, #fafcfb 0%, #f1f5f3 100%)',
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0,0,0,0.08)',
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <DashboardIcon sx={{ fontSize: 56, color: '#52ad83', mb: 1.5 }} />
                <Typography 
                  variant="h4" 
                  component="h2" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Dashboard Overview
                </Typography>
                <Typography variant="body1" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', maxWidth: 600, mx: 'auto' }}>
                  Welcome to the Akros administration center. Select any individual module below or switch tabs above.
                </Typography>
              </Box>

              {/* Quick Launch Cards */}
              <Grid container spacing={2.5}>
                {TABS.slice(1).map((t) => (
                  <Grid item xs={12} sm={6} md={3} key={t.key}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0,0,0,0.08)',
                        background: isDarkMode ? '#1e293b' : '#fafcfb',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: isDarkMode ? '0 8px 24px rgba(82, 173, 131, 0.25)' : '0 8px 24px rgba(82, 173, 131, 0.15)',
                          borderColor: '#52ad83',
                        }
                      }}
                    >
                      <CardActionArea 
                        onClick={() => handleNavigateToTab(t.path)} 
                        sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            background: isDarkMode ? 'rgba(82, 173, 131, 0.18)' : 'rgba(82, 173, 131, 0.1)',
                            color: '#52ad83',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1.5
                          }}>
                            {t.icon}
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDarkMode ? '#e2e8f0' : '#1e293b', mb: 0.5 }}>
                            {t.label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.8rem', lineHeight: 1.4 }}>
                            {t.desc}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, color: '#52ad83', fontWeight: 600, fontSize: '0.75rem' }}>
                          Open Page <ArrowForward sx={{ fontSize: 14 }} />
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </TabPanel>
          
          <TabPanel value={currentTab} index={1} key={`panel-1-${refreshKey}`}>
            <Users />
          </TabPanel>
          
          <TabPanel value={currentTab} index={2} key={`panel-2-${refreshKey}`}>
            <Merchants />
          </TabPanel>
          
          <TabPanel value={currentTab} index={3} key={`panel-3-${refreshKey}`}>
            <Shops />
          </TabPanel>

          <TabPanel value={currentTab} index={4} key={`panel-4-${refreshKey}`}>
            <Categories />
          </TabPanel>

          <TabPanel value={currentTab} index={5} key={`panel-5-${refreshKey}`}>
            <Bookings />
          </TabPanel>

          <TabPanel value={currentTab} index={6} key={`panel-6-${refreshKey}`}>
            <Hotspots />
          </TabPanel>

          <TabPanel value={currentTab} index={7} key={`panel-7-${refreshKey}`}>
            <TestAccounts />
          </TabPanel>

          <TabPanel value={currentTab} index={8} key={`panel-8-${refreshKey}`}>
            <Logs />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
}

export default Dashboard;
